import { SITE_PHOTOS } from '../sitePhotos';
import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSiteInfo } from '../context/SiteContext';
import Sidebar from '../components/Sidebar';
import { publicCollection } from '../lib/publicContent';
import { cleanQuillHtml } from '../lib/imageCompression';

export default function Recruitment() {
  const { siteInfo } = useSiteInfo();
  const [pageContent, setPageContent] = useState<string>('');
  const heroImage = SITE_PHOTOS[2].url;

  useEffect(() => {
    const fetchPageContent = async () => {
      try {
        const page = (await publicCollection('pages')).find(p=>p.id==='recruitment');
        if(page?.content) setPageContent(cleanQuillHtml(page.content));
      } catch (error) {
        console.error('Error fetching recruitment page content:', error);
      }
    };
    fetchPageContent();
  }, []);

  return (
    <div className="container mx-auto px-4 py-8 md:py-12 flex flex-col md:flex-row gap-8">
      <Helmet>
        <title>Tuyển dụng - {siteInfo.name}</title>
        <meta name="description" content={`Gia nhập đội ngũ ${siteInfo.name} - Môi trường làm việc chuyên nghiệp, thu nhập hấp dẫn.`} />
        <link rel="canonical" href={`${siteInfo.baseUrl || "https://khoancocnhoivietnam.vn"}/tuyen-dung`} />
      </Helmet>

      <div className="w-full md:w-3/4 min-w-0">
        <nav className="text-sm text-gray-500 mb-6 flex flex-wrap items-center gap-x-2 gap-y-1">
          <a href="/" className="hover:text-primary whitespace-nowrap">Trang chủ</a>
          <span>/</span>
          <span className="text-gray-800 font-medium break-words max-w-full">Tuyển dụng</span>
        </nav>

        <article className="bg-white p-6 md:p-8 rounded-lg shadow-sm border prose max-w-none break-words">
          <h1 className="text-2xl md:text-3xl font-bold text-primary mb-8 pb-2 border-b-2 border-primary inline-block">
            Thông tin tuyển dụng
          </h1>

          {pageContent ? (
            <>
              <div className="mb-8 rounded-lg overflow-hidden">
                <img src={heroImage} alt="Tuyển dụng" className="w-full h-auto object-contain" />
              </div>
              <div className="article-content ql-editor break-words" dangerouslySetInnerHTML={{ __html: cleanQuillHtml(pageContent) }} />
            </>
          ) : (
            <>
              <div className="mb-8 rounded-lg overflow-hidden">
                <img 
                  src={heroImage} 
                  alt="Tuyển dụng" 
                  className="w-full h-auto object-contain"
                />
              </div>

              <p className="text-lg font-medium text-gray-700 mb-6">
                <strong>{siteInfo.name}</strong> liên tục mở rộng hoạt động kinh doanh và luôn chào đón những ứng viên tài năng, nhiệt huyết gia nhập đội ngũ của chúng tôi.
              </p>

              <h3 className="text-xl font-bold text-primary mt-8 mb-4">Vị trí đang tuyển dụng:</h3>
              
              <div className="space-y-6">
                {/* Vị trí 1 */}
                <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h4 className="text-lg font-bold text-gray-800 mb-2">1. Kỹ sư Giám sát Công trình (Số lượng: 02)</h4>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                    <li><strong>Mô tả công việc:</strong> Giám sát thi công khoan cọc nhồi, ép cừ tại các công trình. Đảm bảo tiến độ, chất lượng và an toàn lao động. Làm việc trực tiếp với chủ đầu tư và các bên liên quan.</li>
                    <li><strong>Yêu cầu:</strong> Tốt nghiệp Đại học/Cao đẳng chuyên ngành Xây dựng, Địa chất công trình. Có ít nhất 1 năm kinh nghiệm giám sát thi công nền móng. Chịu được áp lực công việc, có thể đi công tác.</li>
                    <li><strong>Quyền lợi:</strong> Mức lương thỏa thuận (15 - 25 triệu/tháng) + Thưởng theo dự án. Đóng BHXH đầy đủ. Môi trường làm việc chuyên nghiệp.</li>
                  </ul>
                </div>

                {/* Vị trí 2 */}
                <div className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                  <h4 className="text-lg font-bold text-gray-800 mb-2">2. Công nhân Vận hành Máy Khoan (Số lượng: 05)</h4>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                    <li><strong>Mô tả công việc:</strong> Vận hành máy khoan cọc nhồi mini, máy ép cừ theo sự phân công của kỹ sư giám sát. Bảo dưỡng máy móc định kỳ.</li>
                    <li><strong>Yêu cầu:</strong> Có sức khỏe tốt, chăm chỉ, trung thực. Ưu tiên người có kinh nghiệm vận hành máy khoan, máy xúc. Chưa có kinh nghiệm sẽ được đào tạo.</li>
                    <li><strong>Quyền lợi:</strong> Lương cơ bản + Phụ cấp công trình (Tổng thu nhập 12 - 18 triệu/tháng). Bao ăn ở tại công trình. Thưởng lễ, Tết.</li>
                  </ul>
                </div>
              </div>

              <h3 className="text-xl font-bold text-primary mt-8 mb-4">Cách thức ứng tuyển:</h3>
              <p className="mb-4">Ứng viên quan tâm vui lòng gửi hồ sơ (CV) về địa chỉ email hoặc liên hệ trực tiếp qua số điện thoại:</p>
              <ul className="list-disc pl-6 space-y-2 bg-gray-50 p-6 rounded-lg border border-gray-200">
                <li><strong>Email:</strong> {siteInfo.email} (Tiêu đề ghi rõ: Ứng tuyển [Vị trí] - [Họ và tên])</li>
                <li><strong>Hotline/Zalo:</strong> {siteInfo.phone} (Mr. Dự)</li>
                <li><strong>Địa chỉ nộp hồ sơ trực tiếp:</strong> {siteInfo.address}</li>
              </ul>
              <p className="mt-4 text-sm text-gray-500 italic">Lưu ý: Chúng tôi chỉ liên hệ phỏng vấn những hồ sơ đạt yêu cầu. Không hoàn trả hồ sơ nếu không trúng tuyển.</p>
            </>
          )}

        </article>
      </div>
      
      <div className="w-full md:w-1/4 md:shrink-0">
        <Sidebar />
      </div>
    </div>
  );
}

